// Voice Campaigns — one module, three variants.
//
// Consolidates what used to be three separate nav entries + pages (AI campaign
// creation, OBD broadcast, and standard campaign management) into a single
// tabbed surface. The existing flows are reused as-is under each tab rather than
// rewritten — same behaviour, one entry point.
//
// The active tab is driven by ?variant= so links and the sidebar can deep-link
// straight to a variant, and the old routes redirect here with the right tab.
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bot, Radio, Megaphone } from 'lucide-react';
import AIVoiceCampaigns from './AIVoiceCampaigns';
import CampaignManagement from './CampaignManagement';
import { FEATURE_FLAGS } from 'utils/constants';

type Variant = 'ai' | 'obd' | 'standard';

const ALL_TABS: { id: Variant; label: string; icon: any; hint: string }[] = [
  { id: 'ai', label: 'AI Agent', icon: Bot, hint: 'An AI agent places the calls from a script' },
  { id: 'standard', label: 'Standard', icon: Megaphone, hint: 'Inbound / outbound / hybrid calling campaigns' },
];

// The OBD tab is GONE, not hidden. Bulk outbound dialling folded into the
// campaign itself: `campaigns.call_type` now says whether a call plays a
// recording, waits for a keypress, runs an IVR flow or holds a conversation,
// and /obd/campaigns no longer exists to back a separate screen.
//
// Standard stays flag-hidden, which is a different situation — that flow still
// has a server behind it.
const TABS = ALL_TABS.filter(t =>
  (t.id !== 'standard' || FEATURE_FLAGS.SHOW_STANDARD_CAMPAIGNS)
);

const VoiceCampaigns: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const raw = (params.get('variant') || 'ai') as Variant;
  const active: Variant = TABS.some(t => t.id === raw) ? raw : (TABS[0]?.id ?? 'ai');

  const setActive = (v: Variant) => {
    const next = new URLSearchParams(params);
    next.set('variant', v);
    setParams(next, { replace: true });
  };

  const activeTab = TABS.find(t => t.id === active) ?? ALL_TABS[0];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
          <Megaphone className="w-5 h-5" style={{ color: 'var(--accent)' }} />
          Voice Campaigns
        </h1>
        <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>{activeTab.hint}</p>
      </div>

      {/* variant tabs — only worth drawing when there is a choice to make.
          With OBD and Standard hidden there is one variant, so the row is
          suppressed rather than shown as a single dead tab. */}
      {TABS.length > 1 && (
        <div className="flex gap-1 flex-wrap" role="tablist">
          {TABS.map(t => {
            const Icon = t.icon;
            const on = t.id === active;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={on}
                onClick={() => setActive(t.id)}
                className={`tl-nav-item ${on ? 'tl-nav-item-active' : ''}`}
                style={{ width: 'auto' }}
              >
                <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* variant body. The AI tab is backed by /v1/voice-campaigns (the
          runtime's own engine); Standard remains the existing flow and is
          flag-hidden, so `active` can never select it. */}
      <div>
        {active === 'ai' && <AIVoiceCampaigns />}
        {active === 'standard' && FEATURE_FLAGS.SHOW_STANDARD_CAMPAIGNS && <CampaignManagement />}
      </div>
    </div>
  );
};

export default VoiceCampaigns;
